import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/a3aca552-a4c9-4845-a67b-55cf3e9b7801/images/e117a0c75ac588a3bb30f86b436898948dea70318bbb42071623ebd7636bfda4.png";

const SEED_GALLERY = [
  { url: "https://static.prod-images.emergentagent.com/jobs/a3aca552-a4c9-4845-a67b-55cf3e9b7801/images/dd5c9e3733ea2241512f4db2f06dbead000498ff62fac624a8291adaf8424e78.png", span: "2x2" },
  { url: "https://images.unsplash.com/photo-1472457974886-0ebcd59440cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxsZWdvJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzgwMjQxMDMxfDA&ixlib=rb-4.1.0&q=85", span: "1x1" },
  { url: "https://images.unsplash.com/photo-1563823263008-ec7877629ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxsZWdvJTIwdG95JTIwYWN0aW9ufGVufDB8fHx8MTc4MDI0MTAzMXww&ixlib=rb-4.1.0&q=85", span: "1x2" },
  { url: "https://images.unsplash.com/photo-1538448174498-9956c159edb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxsZWdvJTIwdG95JTIwYWN0aW9ufGVufDB8fHx8MTc4MDI0MTAzMXww&ixlib=rb-4.1.0&q=85", span: "1x1" },
  { url: "https://images.unsplash.com/photo-1472457897821-70d3819a0e24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxsZWdvJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzgwMjQxMDMxfDA&ixlib=rb-4.1.0&q=85", span: "2x1" },
  { url: "https://images.pexels.com/photos/37088149/pexels-photo-37088149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "1x1" },
];

function photoSrc(p) {
  if (p.image_base64) return `data:${p.mime_type || "image/jpeg"};base64,${p.image_base64}`;
  return p.url;
}

function PhotoTile({ p, index, onClick }) {
  const span = p.span || "1x1";
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={`gallery-photo-${index}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.5, ease: "easeOut" }}
      className={`bento-${span} relative overflow-hidden border-2 border-black bg-zinc-100 group focus:outline-none focus:ring-4 focus:ring-[#FFD600]`}
      style={{ aspectRatio: span === "1x1" || span === "2x2" ? "1 / 1" : span === "2x1" ? "2 / 1" : "1 / 2" }}
    >
      <img
        src={photoSrc(p)}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
      />
    </motion.button>
  );
}

export default function Home() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get("/photos").then(({ data }) => {
      if (cancel) return;
      if (Array.isArray(data) && data.length > 0) setPhotos(data);
      else setPhotos(SEED_GALLERY.map((s, i) => ({ id: `seed-${i}`, url: s.url, span: s.span })));
    }).catch(() => {
      setPhotos(SEED_GALLERY.map((s, i) => ({ id: `seed-${i}`, url: s.url, span: s.span })));
    }).finally(() => setLoading(false));
    return () => { cancel = true; };
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-white border-b-4 border-black overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-10 pb-8 grid grid-cols-12 gap-6 items-end">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-7 relative z-10"
          >
            <div className="inline-flex items-center gap-2 bg-[#FFD600] border-2 border-black px-3 py-1 mb-6 brutal-shadow-sm">
              <Sparkles size={14} strokeWidth={3} />
              <span className="font-anton uppercase text-sm tracking-wide">LEGO Photography. By a kid. For everyone.</span>
            </div>
            <h1 className="font-anton uppercase leading-[0.85] tracking-tight text-[16vw] sm:text-[12vw] lg:text-[8.5vw]">
              LEGO<br />
              <span className="text-[#FF3B30]">WORLDS</span><br />
              BY <span className="bg-black text-[#FFD600] px-3">SREHAN</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="col-span-12 lg:col-span-5 relative"
          >
            <div className="absolute -inset-2 stripes-bg z-0" />
            <img
              src={HERO_IMAGE}
              alt=""
              className="relative z-10 w-full h-[360px] sm:h-[460px] object-cover border-4 border-black brutal-shadow-lg"
            />
          </motion.div>
        </div>

        {/* marquee strip */}
        <div className="bg-black text-white py-3 overflow-hidden border-t-4 border-black">
          <div className="flex marquee-track whitespace-nowrap font-anton text-2xl uppercase tracking-widest">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex shrink-0">
                {["Bricks", "Worlds", "Color", "Light", "Macro", "Play", "Click", "Build"].map((w, j) => (
                  <span key={`${i}-${j}`} className="mx-6 flex items-center gap-6">
                    {w}
                    <span className="text-[#FFD600]">●</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY (photos only — no captions) */}
      <section className="bg-white" data-testid="gallery-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <h2 className="font-anton uppercase text-5xl sm:text-7xl leading-none">
              The<br />
              <span className="bg-[#FFD600] px-3">Gallery</span>
            </h2>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="brutal-btn"
            >
              Say Hi <ArrowRight size={18} strokeWidth={3} />
            </Link>
          </div>

          {loading ? (
            <div className="font-anton text-2xl uppercase">Loading…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[220px] gap-4">
              {photos.map((p, i) => (
                <PhotoTile key={p.id} p={p} index={i} onClick={() => setLightbox(p)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="lightbox-overlay"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img
            src={photoSrc(lightbox)}
            alt=""
            className="max-w-full max-h-full object-contain border-4 border-white"
          />
        </div>
      )}
    </div>
  );
}
